<template>
  <div class="table-wrapper">
    <div class="d-flex justify-space-between align-center my-4 mx-4">
      <div>
        <h3>
          Tiến độ giảng dạy
          <span v-if="periodText" class="ml-2" style="color: #1a73e8">({{ periodText }})</span>
        </h3>
      </div>
      <v-btn outlined color="success" @click="exportExcel"> <v-icon left>mdi-file-excel</v-icon> Xuất Excel </v-btn>
    </div>

    <div class="table-scroll">
      <v-data-table
        :headers="headers"
        :items="teachingPlanes"
        :loading="loading"
        item-key="id"
        disable-sort
        disable-pagination
        hide-default-footer
        class="custom-table"
        loading-text="Đang tải"
        no-data-text="Không có kế hoạch giảng dạy"
      >
        <template #body="{ items }">
          <tbody>
            <tr v-if="items.length === 0">
              <td :colspan="headers.length" class="text-center py-4 text-grey">
                Không có báo cáo tiến độ giảng dạy
              </td>
            </tr>
            <tr v-for="item in items" :key="item.id">
              <td class="class-col text-center">
                <router-link
                  v-if="$auth.policies['view-class'] && item.class"
                  :to="'/class/' + item.class.id"
                  class="no-underline"
                >
                  {{ item.class.title }}
                </router-link>
                <span v-else>{{ (item.class && item.class.title) || '-' }}</span>
              </td>

              <td class="subject-col">{{ (item.subject && item.subject.title) || '-' }}</td>

              <td class="teacher-col">
                <CardTeacherNameOnly v-if="item.teacher" :teacher="item.teacher" link />
                <span v-else style="color: red">Chưa có giáo viên</span>
              </td>

              <td
                v-for="(lesson, lIndex) in item.recentLessons || []"
                :key="`${item.id}-lesson-${lIndex}`"
                class="lesson-cell text-center"
                style="padding: 0px"
              >
                <div
                  class="lesson-inner"
                  style="
                    width: 100%;
                    height: 100%;
                    align-content: center;
                    padding: 0px 20px;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                  "
                  :class="{
                    'warning-cell': lesson.isEmpty,
                    'success-cell': !lesson.isEmpty && lesson.classNote && lesson.classNote.status === 'on-time',
                    'error-cell': !lesson.isEmpty && lesson.classNote && lesson.classNote.status === 'late'
                  }"
                  @mouseenter="showTooltip(lesson, $event)"
                  @mousemove="moveTooltip($event)"
                  @mouseleave="hideTooltip"
                >
                  <span
                    class="lesson-content"
                    :title="lesson.name || 'Chưa có thông tin'"
                    v-if="lesson && !lesson.isEmpty"
                  >
                    {{ lesson.name || 'Chưa có thông tin' }}
                  </span>
                  <span v-else class="lesson-content" :title="lesson.name || 'Chưa có thông tin'">
                    {{ lesson.name || 'Chưa có thông tin' }}
                  </span>

                  <!-- tooltip placed inside same div but positioned via style -->
                  <div
                    class="tooltip"
                    v-if="hoveredLesson === lesson && tooltipVisible"
                    :style="tooltipStyle"
                    ref="tooltip"
                  >
                    <div>
                      <strong>Tiết {{ lesson.week }}</strong>
                    </div>
                    <div>{{ lesson.name }}</div>

                    <div class="tooltip-detail">
                      <v-row dense>
                        <v-col cols="4">
                          <span class="tooltip-label">Loại hình</span>
                          <br />
                          {{ lesson.activityType | teachingPlanActivityTypes }}
                        </v-col>
                        <v-col cols="8">
                          <span class="tooltip-label">Địa điểm</span>
                          <br />
                          {{ lesson.location | teachingPlanLocations }}
                        </v-col>
                      </v-row>
                      <v-row dense>
                        <v-col cols="12">
                          <span class="tooltip-label">Thời điểm dự kiến</span>
                          <br />
                          <span>Tuần {{ lesson.week || '?' }}</span>
                        </v-col>
                      </v-row>
                    </div>

                    <div class="tooltip-status" v-if="lesson.classNote && lesson.classNote.status == 'on-time'">
                      <span class="matched">Đã khớp với KHGĐ</span>
                    </div>
                    <div class="tooltip-status" v-if="lesson.classNote && lesson.classNote.status == 'late'">
                      <span class="unmatched">Chưa khớp với KHGĐ</span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import CardTeacherNameOnly from '@/components/basic/card/CardTeacherNameOnly.vue'
import * as XLSX from 'xlsx-js-style'

export default {
  components: {
    CardTeacherNameOnly
  },
  props: {
    fromPeriod: Number,
    toPeriod: Number,
    periodMode: String
  },
  data() {
    return {
      confirmDialog: false,
      search: '',
      tableOptions: {},
      editDialog: false,
      selectedItem: null,
      hoveredLesson: null,
      tooltipVisible: false,
      tooltipStyle: {
        top: '0px',
        left: '0px'
      }
    }
  },

  async created() {
    // await this.refresh()
  },

  computed: {
    ...mapState('TeachingPlan', ['teachingPlanes', 'loading']),
    ...mapState('constant', ['teachingPlanActivityTypes', 'teachingPlanLocations']),
    periodText() {
      if (this.periodMode === 'recent') return 'Tiết 1-5'
      if (this.fromPeriod && this.toPeriod) return 'Tiết ' + this.fromPeriod + '-' + this.toPeriod
      return ''
    },
    dynamicHeaders() {
      const first = (this.teachingPlanes.length && this.teachingPlanes[0]) || null
      const lessonCount = first && first.recentLessons ? first.recentLessons.length : 0
      return Array.from({ length: lessonCount }, (_, i) => ({
        text: `Tiết ${first && first.recentLessons ? first.recentLessons[i].week : 0 || i + 1}`,
        value: `lesson_${i}`,
        align: 'center',
        class: 'text-center'
      }))
    },

    headers() {
      return [
        { text: 'Lớp', value: 'class.title', align: 'center', class: 'text-center class-col' },
        { text: 'Môn học', value: 'subject.title', class: 'subject-col' },
        { text: 'Giáo viên phụ trách', value: 'teacher', class: 'teacher-col' },
        ...this.dynamicHeaders
      ]
    }
  },

  methods: {
    ...mapActions('TeachingPlan', ['requestPageSettings', 'searchTeachingPlanList', 'removeTeachingPlan']),

    async refresh(query) {
      query = query || {}
      this.searchTeachingPlanList(query)
    },

    exportExcel() {
      this.$loading.active = true

      // 1. Chuẩn bị headers cho dòng đầu
      const excelHeaders = this.headers.map(h => h.text)

      // 2. Chuẩn bị dữ liệu
      const dataRows = this.teachingPlanes.map(item => {
        const row = []

        row.push(item.class && item.class.title ? item.class.title : '-')
        row.push(item.subject && item.subject.title ? item.subject.title : '-')
        row.push(
          item.teacher
            ? (item.teacher.lastName ? item.teacher.lastName : '') +
                ' ' +
                (item.teacher.firstName ? item.teacher.firstName : '')
            : 'Chưa có giáo viên'
        )

        const lessons = item.recentLessons || []
        for (let i = 0; i < lessons.length; i++) {
          const lesson = lessons[i]
          row.push(lesson && lesson.name ? lesson.name : 'Chưa có thông tin')
        }

        return row
      })

      // 3. Gộp vào AOA
      const aoaData = [excelHeaders, ...dataRows]

      // 4. Tạo worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(aoaData)

      // 5. Gán style màu cho các tiết học
      this.teachingPlanes.forEach((item, rowIdx) => {
        const lessons = item.recentLessons || []
        lessons.forEach((lesson, colIdx) => {
          const excelRow = rowIdx + 1 // +1 vì header ở row 0
          const excelCol = 3 + colIdx // +3 vì class/subject/teacher là 3 cột đầu

          const cellRef = XLSX.utils.encode_cell({ r: excelRow, c: excelCol })
          const cell = worksheet[cellRef]
          if (!cell) return

          // Xác định màu theo trạng thái
          let bgColor = null
          if (lesson.isEmpty) {
            bgColor = 'FFF3CD' // Vàng nhạt
          } else if (lesson.classNote && lesson.classNote.status === 'on-time') {
            bgColor = 'E5F6EC' // Xanh nhạt
          } else if (lesson.classNote && lesson.classNote.status === 'late') {
            bgColor = 'FFDAC9' // Đỏ nhạt
          }

          // Gán màu nếu có
          if (bgColor) {
            cell.s = {
              fill: {
                type: 'pattern',
                patternType: 'solid',
                fgColor: { rgb: bgColor }
              }
            }
          }
        })
      })

      // 6. Tạo workbook và export
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tiến độ')

      const fileName = 'BaoCaoTienDo_' + new Date().toISOString().split('T')[0] + '.xlsx'
      XLSX.writeFile(workbook, fileName)

      this.$loading.active = false
    },
    showTooltip(lesson, event) {
      this.hoveredLesson = lesson
      this.tooltipVisible = true
      this.updateTooltipPosition(event)
    },

    moveTooltip(event) {
      if (this.tooltipVisible) {
        this.updateTooltipPosition(event)
      }
    },

    hideTooltip() {
      this.tooltipVisible = false
      this.hoveredLesson = null
    },

    updateTooltipPosition(event) {
      const padding = 8
      const tooltipWidth = 280
      const tooltipHeightEstimate = 180 // ước lượng nếu cần để tránh tràn đáy

      let left = event.clientX + 10 // nhẹ dịch sang phải
      let top = event.clientY + 10 // xuống dưới

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (left + tooltipWidth + padding > viewportWidth) {
        left = viewportWidth - tooltipWidth - padding
      }
      if (top + tooltipHeightEstimate + padding > viewportHeight) {
        top = event.clientY - tooltipHeightEstimate - 10
        if (top < padding) top = padding
      }

      this.tooltipStyle = {
        top: `${top}px`,
        left: `${left}px`,
        position: 'fixed'
      }
    }
  }
}
</script>

<style lang="scss">
.table-wrapper {
  overflow-x: auto;
  position: relative;
}

.table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
  z-index: 0;
}

.custom-table {
  position: relative;
  white-space: nowrap;
  min-width: 100%;
}

.v-data-table >>> thead th {
  text-align: center !important;
  vertical-align: middle;
}

.fixed-col {
  position: sticky;
  background: white;
  z-index: 2;
}
.class-col {
  left: 0;
  min-width: 80px;
  max-width: 200px;
}

.subject-col {
  left: 80px;
  min-width: 120px;
  max-width: 300px;
}

.teacher-col {
  left: 285px;
  min-width: 235px;
  max-width: 400px;
}

.lesson-cell {
  max-width: 160px;
  min-width: 140px;
  white-space: nowrap;
  overflow: visible !important;
  position: relative;
  text-overflow: ellipsis;
  text-align: center;
  border: 0.5px solid transparent;

  &:hover .tooltip {
    display: block;
  }
}

.lesson-cell:hover {
  background-color: white !important;
  border: 0.5px solid #255cc2 !important;
}

.warning-cell {
  background-color: #fff3cd !important;
}
.success-cell {
  background-color: #e5f6ec !important;
}
.error-cell {
  background-color: #ffdac9 !important;
}

.no-underline {
  text-decoration: none;
}

.tooltip {
  display: none;
  position: fixed; /* dùng fixed để không bị clipping bởi ancestor */
  width: 280px;
  background-color: white;
  color: black;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  text-align: left;
  white-space: normal;
  pointer-events: none;
  z-index: 200000 !important; /* rất cao */
  font-size: 13px;
  transition: opacity 0.1s ease-in-out;
  opacity: 1;
}

/* mũi tên */
.tooltip::after {
  content: '';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 8px;
  border-style: solid;
  border-color: transparent transparent white transparent;
}

.tooltip-detail {
  margin-top: 8px;
  font-size: 14px;
  color: #333;
}

.tooltip-label {
  font-weight: bold;
  margin-right: 8px;
  display: inline-block;
  min-width: 80px;
  color: #888;
}

.tooltip-status {
  margin-top: 8px;
  font-size: 14px;
}

.tooltip-status .matched {
  color: #4bca81;
}
.tooltip-status .unmatched {
  color: #ff1d30;
}
.fixed-header {
  position: sticky !important;
  position: -webkit-sticky !important;
  z-index: 9998;
}
</style>
